import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getBanners,
  deleteBanner,
  updateShortStatus,
  editBanner,
} from "../../utils/backend";
import {
  CSpinner,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormInput,
  CRow,
  CCol,
  CFormLabel,
  CButton,
  CCardFooter,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CFormSelect,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionButton,
  CAccordionBody,
  CCarousel,
  CCarouselItem,
  CImage,
  CCarouselCaption,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CInputGroup,
} from "@coreui/react-pro";

import { CPagination, CPaginationItem } from "@coreui/react-pro";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CIcon from "@coreui/icons-react";
import {
  cilSortNumericUp,
  cilSortNumericDown,
  cilSortAlphaUp,
  cilSortAlphaDown,
  cilSwapVertical,
} from "@coreui/icons";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";
import { setUppercase } from "../../utils/SetUppercase";

const Banners = () => {
  const hasPermission = (permissionGroup, permissionName) => {
    let permissions;
    try {
      permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      permissions = [];
    }

    return permissions.some(
      (perm) =>
        perm.permission_group === permissionGroup &&
        perm.permission_name === permissionName
    );
  };

  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [bannerType, setBannerType] = useState("");
  const [fileName, setFileName] = useState("");
  const [sequence, setSequence] = useState("");
  const [status, setStatus] = useState("");
  const [createdAt, setCreatedAt] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  const [updatedFrom, setUpdatedFrom] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalBanners, setTotalBanners] = useState(0);
  const [usedSequences, setUsedSequences] = useState(0);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "desc",
  });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc"; // Toggle back to ascending
    }
    setSortConfig({ key, direction });
    setAuditLogs((prevLogs) => [...prevLogs]); // Trigger re-render to apply styles
  };

  const sortedAuditLogs = React.useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return auditLogs;
    return [...auditLogs].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "status") {
        aValue = a.status === 1 ? "Active" : "Inactive";
        bValue = b.status === 1 ? "Active" : "Inactive";
      }

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [auditLogs, sortConfig]);

  useEffect(() => {
    // Load filters and pagination state from localStorage on page load
    const savedState = JSON.parse(localStorage.getItem("bannersState"));
    if (savedState) {
      setTitle(savedState.title || "");
      setStatus(savedState.status || "");
      setBannerType(savedState.type || "");
      setFileName(savedState.filename || "");
      setSequence(savedState.sequence || "");
      setCreatedAt(savedState.start_date || "");
      setCreatedFrom(savedState.end_date || "");
      setUpdatedAt(savedState.updated_start_date || "");
      setUpdatedFrom(savedState.updated_end_date || "");
      setPerPage(savedState.perPage || 10);

      // Fetch data based on the saved state
      // Always land on page 1. Restoring a saved page number stranded admins on
      // stale rows: the list is sorted newest-first, so page N>1 never shows new
      // rows and nothing on screen explained why.
      const filters = {
        title: savedState.title || "",
        status: savedState.status || "",
        type: savedState.type || "",
        filename: savedState.filename || "",
        sequence: savedState.sequence || "",
        start_date: savedState.start_date || "",
        end_date: savedState.end_date || "",
        updated_start_date: savedState.updated_start_date || "",
        updated_end_date: savedState.updated_end_date || "",
        page: 1,
        perPage: savedState.perPage || 10,
      };
      fetchData(filters);
    } else {
      fetchData();
    }
  }, []);

  const saveStateToLocalStorage = () => {
    const existingState =
      JSON.parse(localStorage.getItem("bannersState")) || {};
    // Evict keys written by older builds; the merge below would otherwise keep a
    // stale currentPage alive in every admin's browser indefinitely.
    delete existingState.currentPage;
    const state = {
      title,
      type: bannerType,
      filename: fileName,
      sequence,
      status,
      start_date: createdAt,
      end_date: createdFrom,
      updated_start_date: updatedAt,
      updated_end_date: updatedFrom,
    };
    localStorage.setItem(
      "bannersState",
      JSON.stringify({ ...existingState, ...state })
    );
  };

  const fetchData = async (filters = { per_page: perPage }) => {
    //setIsLoading(true);
    try {
      const response = await getBanners(filters);
      console.log(response)
      setAuditLogs(setUppercase(response.data.data));
      setTotalBanners(response.data.data.length);
      const sequences = response.data.data.map((banner) => banner.sequence);
      setUsedSequences(sequences);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalItems(response.data.totalItems);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAuditLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    const filters = {
      title,
      type: bannerType,
      filename: fileName,
      sequence,
      status,
      start_date: createdAt,
      end_date: createdFrom,
      updated_start_date: updatedAt,
      updated_end_date: updatedFrom,
      page: 1,
      perPage: perPage,
    };
    saveStateToLocalStorage(); // Save state before fetching data
    await fetchData(filters);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    title,
    bannerType,
    fileName,
    sequence,
    status,
    createdAt,
    createdFrom,
    updatedAt,
    updatedFrom,
    perPage,
  ]);

  const handleReset = () => {
    setTitle("");
    setBannerType("");
    setFileName("");
    setSequence("");
    setStatus("");
    setCreatedAt("");
    setCreatedFrom("");
    setUpdatedAt("");
    setUpdatedFrom("");
    setCurrentPage(1);

    // Retain only perPage in localStorage
    const existingState =
      JSON.parse(localStorage.getItem("bannersState")) || {};
    localStorage.setItem(
      "bannersState",
      JSON.stringify({ perPage: existingState.perPage || 10 })
    );

    fetchData();
  };

  const handlePageChange = async (page) => {
    const filters = {
      title,
      type: bannerType,
      filename: fileName,
      sequence,
      status,
      start_date: createdAt,
      end_date: createdFrom,
      updated_start_date: updatedAt,
      updated_end_date: updatedFrom,
      page: page,
      perPage: perPage,
    };
    await fetchData(filters);
  };

  const handlePerPageChange = async (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to the first page
    localStorage.setItem(
      "bannersState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("bannersState")),
        perPage: newPerPage,
      })
    );
    const filters = {
      title,
      type: bannerType,
      filename: fileName,
      sequence,
      status,
      start_date: createdAt,
      end_date: createdFrom,
      updated_start_date: updatedAt,
      updated_end_date: updatedFrom,
      page: 1,
      perPage: newPerPage,
    };
    await fetchData(filters);
  };

  const handleDelete = async (id) => {
    try {
      await deleteBanner(id);
      toast.success("Banner deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.message);
      console.error("Failed to delete banner:", error);
    } finally {
      setDeleteModalVisible(false);
      setBannerToDelete(null);
    }
  };

  const confirmDelete = (id) => {
    setBannerToDelete(id);
    setDeleteModalVisible(true);
  };

  const handleStatusChange = async (id, status) => {
    const formData = new FormData();

    // Find the original banner and store its status
    const originalBanner = auditLogs.find((banner) => banner.id === id);
    const originalStatus = originalBanner ? originalBanner.status : null;

    // If the new status is the same as the current status, do nothing
    if (originalStatus === Number(status)) {
      return;
    } else {
      formData.append("status", Number(status));
      formData.append("sequence", originalBanner.sequence);
    }

    // Optimistically update the UI
    setAuditLogs((prevLogs) =>
      prevLogs.map((banner) =>
        banner.id === id ? { ...banner, status: Number(status) } : banner
      )
    );

    try {
      const response = await editBanner(formData, id);
      if (response.success) {
        toast.success("Banner status updated successfully");
      } else {
        throw new Error(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating status");

      // Revert the status if the update fails
      setAuditLogs((prevLogs) =>
        prevLogs.map((banner) =>
          banner.id === id ? { ...banner, status: originalStatus } : banner
        )
      );
    }
  };

  const generatePerPageOptions = (totalItems) => {
    if (totalItems <= 10) {
      return [10];
    }
    const options = [];
    let interval = 10;
    while (interval < totalItems) {
      options.push(interval);
      if (interval >= 50) {
        interval += 50;
      } else {
        interval *= 5;
      }
    }
    const nearestHigherMultipleOf50 = Math.ceil(totalItems / 50) * 50;
    options.push(nearestHigherMultipleOf50);
    return options;
  };

  // Surfaced next to Reset so an admin can never be silently filtered down to a subset.
  const activeFilterCount = [
    title,
    bannerType,
    fileName,
    sequence,
    status,
    createdAt,
    createdFrom,
    updatedAt,
    updatedFrom,
  ].filter(function (v) {
    return v !== "" && v !== null && v !== undefined;
  }).length;

  return (
    <>
      <style>
        {`
          .banner-image {
            max-height: 300px;
            object-fit: contain;
          }
        `}
      </style>
      <CCard className="mb-4 border-light">
        <CAccordion alwaysOpen activeItemKey={1}>
          <CAccordionItem itemKey={1} visible={true}>
            <CAccordionHeader>Carousel Preview</CAccordionHeader>
            <CAccordionBody>
              <CCardBody style={{ overflowX: "auto" }}>
                {isLoading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "300px",
                    }}
                  >
                    <Skeleton width={600} height={300} />
                  </div>
                ) : (
                  <div
                    style={{
                      overflowX: "auto",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <CCarousel
                      controls
                      indicators
                      className="w-50 m-4"
                      touch
                      pause="hover"
                      wrap={true}
                      transition="crossfade"
                    >
                      {auditLogs
                        .filter((banner) => banner.status === 1)
                        .map((banner, index) => (
                          <CCarouselItem key={banner.id || index}>
                            <CImage
                              className="d-block w-100 banner-image"
                              src={banner.image}
                              alt={`slide ${index + 1}`}
                            />
                            <CCarouselCaption className="d-none d-md-block">
                              <h5>{banner.title}</h5>
                            </CCarouselCaption>
                          </CCarouselItem>
                        ))}
                    </CCarousel>
                  </div>
                )}
              </CCardBody>
            </CAccordionBody>
          </CAccordionItem>
        </CAccordion>
      </CCard>
      <CCard className="border-light">
        <CCardBody style={{ overflowX: "auto" }}>
          {isLoading ? (
            <div>
              <Skeleton height={40} count={1} />
              <Skeleton height={30} count={5} style={{ marginTop: "10px" }} />
            </div>
          ) : (
            <>
              <CRow>
                <CCol className="justify-content-end d-flex my-1 gap-3 align-items-center">
                  {activeFilterCount > 0 && (
                    <span
                      className="me-3 text-nowrap"
                      style={{
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffe69c",
                        borderRadius: "12px",
                        color: "#664d03",
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        padding: "4px 10px",
                      }}
                    >
                      <i className="bi bi-funnel-fill me-1"></i>
                      {activeFilterCount}{" "}
                      {activeFilterCount === 1 ? "filter" : "filters"} active —
                      showing a subset
                    </span>
                  )}
                  {hasPermission("Carousel Banner Management", "Create") && (
                    <CButton
                      className="ansa-button-primary"
                      onClick={() =>
                        navigate("/banners/create", {
                          state: {
                            totalBanners: totalBanners,
                            usedSequences: usedSequences,
                          },
                        })
                      }
                    >
                      Create New Banner
                    </CButton>
                  )}
                  <CButton
                    className="ansa-button-secondary me-3"
                    type="button"
                    onClick={handleReset}
                  >
                    Reset
                  </CButton>
                </CCol>
              </CRow>
              <div style={{ overflowX: "auto" }}>
                <CTable striped borderless>
                  <CTableHead>
                    <CTableRow>
                      <CTableDataCell></CTableDataCell>
                      <CTableDataCell></CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={bannerType}
                          onChange={(e) => setBannerType(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={fileName}
                          onChange={(e) => setFileName(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={sequence}
                          onChange={(e) => setSequence(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormSelect
                          id="status"
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                        >
                          <option value="">Select Status</option>
                          <option value="1">Active</option>
                          <option value="0">Inactive</option>
                        </CFormSelect>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CInputGroup style={{ width: "250px" }}>
                          <CFormInput
                            type="date"
                            value={createdAt}
                            onChange={(e) => setCreatedAt(e.target.value)}
                            title="Start Date"
                          />

                          <span className="input-group-text bg-light text-muted small">
                            to
                          </span>

                          <CFormInput
                            type="date"
                            value={createdFrom}
                            onChange={(e) => setCreatedFrom(e.target.value)}
                            title="End Date"
                          />
                        </CInputGroup>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CInputGroup style={{ width: "250px" }}>
                          <CFormInput
                            type="date"
                            value={updatedAt}
                            onChange={(e) => setUpdatedAt(e.target.value)}
                            title="Start Date"
                          />

                          <span className="input-group-text bg-light text-muted small">
                            to
                          </span>

                          <CFormInput
                            type="date"
                            value={updatedFrom}
                            onChange={(e) => setUpdatedFrom(e.target.value)}
                            title="End Date"
                          />
                        </CInputGroup>
                      </CTableDataCell>
                    </CTableRow>
                    <CTableRow>
                      <CTableHeaderCell
                        onClick={() => {}}
                        className={`hover-header ${
                          sortConfig.key === "id" ? "ctable-active-header" : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "80px",
                        }}
                      >
                        No.{" "}
                        {sortConfig.key === "id" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortNumericDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortNumericUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell>Actions</CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("title")}
                        className={`hover-header ${
                          sortConfig.key === "title"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "150px",
                        }}
                      >
                        Title{" "}
                        {sortConfig.key === "title" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortAlphaDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortAlphaUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("type")}
                        className={`hover-header ${
                          sortConfig.key === "type"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "120px",
                        }}
                      >
                        Type{" "}
                        {sortConfig.key === "type" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortAlphaDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortAlphaUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("image")}
                        className={`hover-header ${
                          sortConfig.key === "image"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "150px",
                        }}
                      >
                        Image{" "}
                        {sortConfig.key === "image" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortAlphaDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortAlphaUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("sequence")}
                        className={`hover-header ${
                          sortConfig.key === "sequence"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "120px",
                        }}
                      >
                        Sequence{" "}
                        {sortConfig.key === "sequence" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortNumericDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortNumericUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("status")}
                        className={`hover-header ${
                          sortConfig.key === "status"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "120px",
                        }}
                      >
                        Status{" "}
                        {sortConfig.key === "status" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortAlphaDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortAlphaUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("created_at")}
                        className={`hover-header ${
                          sortConfig.key === "created_at"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "150px",
                        }}
                      >
                        Created At{" "}
                        {sortConfig.key === "created_at" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortNumericDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortNumericUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("updated_at")}
                        className={`hover-header ${
                          sortConfig.key === "updated_at"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "150px",
                        }}
                      >
                        Updated At{" "}
                        {sortConfig.key === "updated_at" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortNumericDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortNumericUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {sortedAuditLogs.map((banner, index) => (
                      <CTableRow key={banner.id}>
                        <CTableDataCell>
                          {index + 1 + perPage * (currentPage - 1)}
                        </CTableDataCell>
                        <CTableDataCell>
                          <div className="d-flex align-items-center gap-2">
                            {hasPermission(
                              "Carousel Banner Management",
                              "View"
                            ) && (
                              <CButton
                                className="btn btn-sm btn-info text-white"
                                title="Details"
                                onClick={() =>
                                  navigate(`/banners/details/${banner.id}`, {
                                    state: {
                                      totalBanners: totalBanners,
                                      usedSequences: usedSequences,
                                    },
                                  })
                                }
                              >
                                <i className="bi bi-eye-fill"></i>
                              </CButton>
                            )}
                            {hasPermission(
                              "Carousel Banner Management",
                              "Edit"
                            ) && (
                              <CButton
                                className="btn btn-sm btn-warning text-white"
                                title="Edit"
                                onClick={() =>
                                  navigate(`/banners/edit/${banner.id}`, {
                                    state: {
                                      totalBanners: totalBanners,
                                      usedSequences: usedSequences,
                                    },
                                  })
                                }
                              >
                                <i className="bi bi-pencil-fill"></i>
                              </CButton>
                            )}
                            {hasPermission(
                              "Carousel Banner Management",
                              "Delete"
                            ) && (
                              <CButton
                                className="btn btn-sm btn-danger text-white"
                                title="Delete"
                                onClick={() => confirmDelete(banner.id)}
                              >
                                <i className=" bi bi-trash-fill"></i>
                              </CButton>
                            )}
                          </div>
                        </CTableDataCell>
                        <CTableDataCell>{banner.title || "N/A"}</CTableDataCell>
                        <CTableDataCell>{banner.type || "N/A"}</CTableDataCell>
                        <CTableDataCell>
                          {banner.image ? (
                            <a
                              href={banner.image}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {banner.image.split("/").pop()}
                            </a>
                          ) : (
                            "N/A"
                          )}
                        </CTableDataCell>
                        <CTableDataCell>
                          {banner.sequence || "N/A"}
                        </CTableDataCell>
                        {/* <CTableDataCell>{banner.status || "N/A"}</CTableDataCell> */}
                        <CTableDataCell>
                          <CDropdown>
                            <CDropdownToggle
                              color="secondary"
                              variant="outline"
                              disabled={
                                !hasPermission(
                                  "Carousel Banner Management",
                                  "Edit"
                                )
                              }
                              style={{
                                backgroundColor:
                                  banner.status === 1 ? "#66BB6A" : "#A9A9A9",
                                color: "#fff",
                                border: "none",
                                width: "150px",
                                borderRadius: "20px",
                                fontWeight: "500",
                                fontSize: "14px",
                              }}
                            >
                              {banner.status === 1 ? "ACTIVE" : "INACTIVE"}
                            </CDropdownToggle>
                            <CDropdownMenu>
                              <CDropdownItem
                                onClick={() =>
                                  handleStatusChange(banner.id, "1")
                                }
                              >
                                ACTIVE
                              </CDropdownItem>
                              <CDropdownItem
                                onClick={() =>
                                  handleStatusChange(banner.id, "0")
                                }
                              >
                                INACTIVE
                              </CDropdownItem>
                            </CDropdownMenu>
                          </CDropdown>
                        </CTableDataCell>
                        <CTableDataCell style={{ minWidth: "110px" }}>
                          {banner.created_at
                            ? formatDateTime(banner.created_at)
                            : "N/A"}
                        </CTableDataCell>
                        <CTableDataCell style={{ minWidth: "110px" }}>
                          {banner.updated_at
                            ? formatDateTime(banner.updated_at)
                            : "N/A"}
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              </div>
            </>
          )}

          {!isLoading && (
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div>
                <CPagination
                  className="mx-2"
                  aria-label="Page navigation example"
                  onActivePageChange={async (page) => {
                    setCurrentPage(page);
                    await fetchData({
                      title: title || null,
                      status: status || null,
                      page: currentPage,
                      per_page: perPage,
                    });
                  }}
                  activePage={currentPage}
                  pages={totalPages}
                  doubleArrows={false}
                  arrows
                >
                  {/* ...existing pagination items... */}
                  <CPaginationItem
                    aria-label="Previous"
                    disabled={currentPage <= 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </CPaginationItem>
                  {[...Array(totalPages).keys()].map((pageNumber) => {
                    if (
                      pageNumber === 0 || // First page
                      pageNumber === totalPages - 1 || // Last page
                      pageNumber < 5 || // First few pages
                      (pageNumber >= currentPage - 2 &&
                        pageNumber <= currentPage + 2) // Pages around the current page
                    ) {
                      return (
                        <CPaginationItem
                          key={pageNumber + 1}
                          active={currentPage === pageNumber + 1}
                          onClick={() => handlePageChange(pageNumber + 1)}
                        >
                          {pageNumber + 1}
                        </CPaginationItem>
                      );
                    } else if (
                      (pageNumber === 5 && currentPage > 7) || // Ellipsis after the first few pages
                      (pageNumber === currentPage + 3 &&
                        currentPage < totalPages - 4) // Ellipsis before the last page
                    ) {
                      return (
                        <CPaginationItem
                          key={`ellipsis-${pageNumber}`}
                          disabled
                        >
                          ...
                        </CPaginationItem>
                      );
                    } else {
                      return null;
                    }
                  })}
                  <CPaginationItem
                    aria-label="Next"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </CPaginationItem>
                </CPagination>
              </div>
              <div className="d-flex align-items-center">
                <span className="me-3">
                  Showing {auditLogs.length} of {totalItems} banners
                </span>
                <CFormSelect
                  className="w-auto"
                  value={perPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                >
                  {generatePerPageOptions(totalItems).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </CFormSelect>
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
      <CModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        alignment="center"
      >
        <CModalHeader>
          <CModalTitle>Confirm Delete</CModalTitle>
        </CModalHeader>
        <CModalBody>Are you sure you want to delete this banner?</CModalBody>
        <CModalFooter>
          <CButton
            className="ansa-button-secondary"
            onClick={() => setDeleteModalVisible(false)}
          >
            Cancel
          </CButton>
          <CButton
            className="ansa-button-primary"
            onClick={() => handleDelete(bannerToDelete)}
          >
            Delete
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default Banners;
